import React, { useState } from "react";
import PropTypes from "prop-types";
import Step2 from "../RegistrationForm/Step2.jsx";
import Step3 from "../RegistrationForm/Step3.jsx";

const MemberProfile = ({
  howDidYouHearItems,
  countries,
  states,
  underwriterOptions,
  dataSourceId,
  profile,
  dictionary,
}) => {
  // ----------------------------
  // Dictionary items - from div mount attribute
  //

  //Submit button on step 3
  const [submitButton, setSubmitButton] = useState(
    dictionary?.updateProfileButton || "Create Profile",
  );
  // ----------------------------
  // Field state - prefilled from profile
  // ----------------------------
  const [email, setEmail] = useState(profile?.emailAddress || "");
  const [firstName, setFirstName] = useState(profile?.firstName || "");
  const [lastName, setLastName] = useState(profile?.lastName || "");
  const [agencyId, setAgencyId] = useState(profile?.agencyId || "");
  const [underwriter, setUnderwriter] = useState(profile?.underwriter || "");

  // Create verification data object from profile
  const [verificationData, setVerificationData] = useState({
    companyName: profile?.companyName || "",
    streetAddress1: profile?.streetAddress1 || "",
    streetAddress2: profile?.streetAddress2 || "",
    city: profile?.city || "",
    addressState: profile?.addressState || "", // Fix: use 'state' not 'addressState'
    zipCode: profile?.zipCode || "",
    fax: profile?.fax || "",
    phoneNumber: profile?.phoneNumber || "",
    jobTitle: profile?.jobTitle || "",
    country: profile?.country || "", // Add missing country field
  });

  const [selectedHowDidYouHear, setSelectedHowDidYouHear] = useState(
    profile?.howDidYouHearAboutUs?.map(item => item.toUpperCase()) || [],
  );

  const [howDidYouHearOther, setHowDidYouHearOther] = useState(
    profile?.howDidYouHearAboutUsOtherText || "",
  );
  const [marketingContactName, setMarketingContactName] = useState(
    profile?.marketingContact?.name || "",
  );
  const [marketingContactPhone, setMarketingContactPhone] = useState(
    profile?.marketingContact?.phone || "",
  );
  const [marketingContactEmail, setMarketingContactEmail] = useState(
    profile?.marketingContact?.email || "",
  );

  // ----------------------------
  // Messages & errors
  // ----------------------------
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ----------------------------
  // Validation (centralized)
  // ----------------------------
  const req = v =>
    !v || !v.toString().trim() ? "Please fill out this field." : "";
  const sel = (v, label) => (!v ? `Please select ${label}.` : "");

  const pwVal = v => {
    const r = req(v);
    if (r) return r;
    if (v.length < 6) return "Password must be at least 6 characters long.";
    const kinds = [
      /[A-Z]/,
      /[a-z]/,
      /[0-9]/,
      /[!@#$%^&*()\-_=+\[\]|;:,.{}]/,
    ].reduce((n, rx) => n + (rx.test(v) ? 1 : 0), 0);
    if (kinds < 3) {
      return "The password must be at least 6 characters and have 3 of: Uppercase, Lowercase, Numerals, Punctuation.";
    }
    return "";
  };

  const fieldIdFor = key =>
    ({
      email: "email",
      agency_id: "agency-id",
      underwriter: "underwriter",
      first_name: "first-name",
      last_name: "last-name",
      job_title: "job-title",
      country: "country",
      phone: "phone",
      zip: "zip",
      password: "password",
      new_password: "new-password",
      confirm_password: "confirm-password",
    })[key] || key;

  const scrollToError = id => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  // validators map: keys match input name=
  const validators = {
    first_name: v => req(v),
    last_name: v => req(v),
    password: (v, all) => (all.new_password ? req(v) : ""), // Only required if new_password is filled
    new_password: v => (v ? pwVal(v) : ""), // Only validate if filled
    confirm_password: (v, all) => {
      if (!all.new_password) return ""; // Not required if no new password
      if (!v) return "Please fill out this field.";
      return v !== all.new_password ? "Passwords do not match." : "";
    },
    job_title: v => req(v),
    country: v => sel(v, "a country"),
    phone: v => req(v),
    zip: (v, all) => (all.country === "US" ? req(v) : ""),
  };

  const formToObject = formEl => {
    const fd = new FormData(formEl);
    const obj = {};
    fd.forEach((val, key) => {
      obj[key] = val;
    });
    return obj;
  };

  const validateForm = formEl => {
    const values = formToObject(formEl);
    const nextErrors = {};
    Object.keys(validators).forEach(name => {
      const fn = validators[name];
      const msg = fn ? fn(values[name], values) : "";
      if (msg) nextErrors[name] = msg;
    });
    return { values, errors: nextErrors };
  };

  const clearFieldError = key => setErrors(p => ({ ...p, [key]: "" }));

  // ----------------------------
  // Change handlers (clear errors inline)
  // ----------------------------
  const handleFirstNameChange = e => {
    setFirstName(e.target.value);
    clearFieldError("first_name");
  };
  const handleLastNameChange = e => {
    setLastName(e.target.value);
    clearFieldError("last_name");
  };

  // Disabled handlers for agency ID and underwriter (no-op)
  const handleCompanyAgencyIdChange = () => {};
  const handleCompanyUnderwriterChange = () => {};

  const handleMarketingContactNameChange = e =>
    setMarketingContactName(e.target.value);
  const handleMarketingContactPhoneChange = e =>
    setMarketingContactPhone(e.target.value);
  const handleMarketingContactEmailChange = e =>
    setMarketingContactEmail(e.target.value);

  const handleHowDidYouHearChange = (value, checked) => {
    if (checked) setSelectedHowDidYouHear(prev => [...prev, value]);
    else setSelectedHowDidYouHear(prev => prev.filter(v => v !== value));
  };
  const handleHowDidYouHearOtherChange = e =>
    setHowDidYouHearOther(e.target.value);

  // ----------------------------
  // Submit
  // ----------------------------
  const handleFormSubmit = async e => {
    e.preventDefault();

    const { errors: nextErrors } = validateForm(e.target);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      const firstKey = Object.keys(nextErrors)[0];
      scrollToError(fieldIdFor(firstKey));
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const formData = new FormData(e.target);
      const payload = {
        UserName: email,
        FirstName: formData.get("first_name"),
        LastName: formData.get("last_name"),
        EmailAddress: email,
        AgencyId: agencyId || "",
        Underwriter: underwriter || "",
        companyName: formData.get("companyname"),
        streetAddress1: formData.get("streetaddress1"),
        streetAddress2: formData.get("streetaddress2"),
        City: formData.get("city"),
        addressState: formData.get("addressstate"),
        zipCode: formData.get("zip"),
        Country: formData.get("country"),
        phoneNumber: formData.get("phone"),
        Fax: formData.get("fax"),
        JobTitle: formData.get("job_title"),
        MarketingContact: {
          Name: formData.get("marketing_contact_name"),
          Phone: formData.get("marketing_contact_phone"),
          Email: formData.get("marketing_contact_email"),
        },
        UserType: verificationData?.userType || "",
        DatasourceId: formData.get("datasource_id"),
        HowDidYouHearAboutUs: selectedHowDidYouHear,
        HowDidYouHearAboutUsOtherText: formData.get("how-did-you-hear-other"),
      };

      // Add password fields if they exist
      const currentPassword = formData.get("password");
      const newPassword = formData.get("new_password");
      const confirmPassword = formData.get("confirm_password");

      if (currentPassword) {
        payload.CurrentPassword = currentPassword;
      }
      if (newPassword) {
        payload.NewPassword = newPassword;
      }
      if (confirmPassword) {
        payload.ConfirmPassword = confirmPassword;
      }

      const response = await fetch("/api/Profile/UpdateProfile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitSuccess(true);
        setErrors({});

        setTimeout(() => {
          const successElement = document.querySelector(
            ".registration-form__success",
          );
          if (successElement) {
            successElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      } else {
        let errorMsg =
          result.errorMessage ||
          result.message ||
          "Profile update failed. Please try again.";
        let parsed = {};
        try {
          if (
            typeof errorMsg === "string" &&
            errorMsg.startsWith("{") &&
            errorMsg.endsWith("}")
          ) {
            parsed = JSON.parse(errorMsg);
          }
        } catch (e) {
          // Not JSON, fallback to string
        }
        if (Object.keys(parsed).length) {
          setErrors(prev => ({ ...prev, ...parsed }));
          const firstKey = Object.keys(parsed)[0];
          scrollToError(fieldIdFor(firstKey));
        } else {
          setSubmitError(errorMsg);
        }
      }
    } catch (error) {
      console.error("Profile update error:", error);
      setSubmitError(
        "An error occurred during profile update. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-form">
      <form
        className="registration-form__form"
        noValidate
        onSubmit={handleFormSubmit}
        aria-label="Member profile form"
      >
        {/* Step 2 - Account Information */}
        <Step2
          firstName={firstName}
          lastName={lastName}
          email={email}
          errors={errors}
          handleFirstNameChange={handleFirstNameChange}
          handleLastNameChange={handleLastNameChange}
          clearFieldError={clearFieldError}
          isVisible={true}
          stepNumber={1}
          totalSteps={2}
          hasTitle={false}
          showNewPassword={true}
        />

        {/* Step 3 - Company Information */}
        <Step3
          countries={countries}
          states={states}
          underwriterOptions={underwriterOptions}
          howDidYouHearItems={howDidYouHearItems}
          agencyId={agencyId}
          underwriter={underwriter}
          verificationData={verificationData}
          selectedHowDidYouHear={selectedHowDidYouHear}
          howDidYouHearOther={howDidYouHearOther}
          marketingContactName={marketingContactName}
          marketingContactPhone={marketingContactPhone}
          marketingContactEmail={marketingContactEmail}
          errors={errors}
          handleCompanyAgencyIdChange={handleCompanyAgencyIdChange}
          handleCompanyUnderwriterChange={handleCompanyUnderwriterChange}
          clearFieldError={clearFieldError}
          handleHowDidYouHearChange={handleHowDidYouHearChange}
          handleHowDidYouHearOtherChange={handleHowDidYouHearOtherChange}
          handleMarketingContactNameChange={handleMarketingContactNameChange}
          handleMarketingContactPhoneChange={handleMarketingContactPhoneChange}
          handleMarketingContactEmailChange={handleMarketingContactEmailChange}
          isVisible={true}
          stepNumber={2}
          totalSteps={2}
          submitSuccess={submitSuccess}
          submitError={submitError}
          isSubmitting={isSubmitting}
          dataSourceId={dataSourceId}
          hasTitle={false}
          submitButtonText={submitButton}
          successMessage="Profile Updated!"
          isMemberProfile={true}
        />
      </form>
    </div>
  );
};

MemberProfile.propTypes = {
  howDidYouHearItems: PropTypes.array,
  countries: PropTypes.array,
  states: PropTypes.array,
  underwriterOptions: PropTypes.array,
  dataSourceId: PropTypes.string,
  profile: PropTypes.object,
};

export default MemberProfile;
